import type {
  CompanyOptionsProfile,
  CompanySettingsProfile,
  PayGroupOptionsProfile,
  PayGroupSettingsProfile,
} from "../../generated/core-api";

// ────────────────────────────────────────────────────────────────────────────────
// Option group
// ────────────────────────────────────────────────────────────────────────────────

/** The two grouping buckets for all company/pay-group option fields. */
export type OptionGroup = "datapay" | "direct-access";

/**
 * Ordered list of provider groups rendered as top-level tabs
 * (`CopySettingsProfile`) / top-level blocks (`ProfileSummary`).
 *
 * Single source of truth — both components map over this instead of each
 * declaring their own copy, so adding/reordering a group can't drift between
 * the editable form and the read-only summary.
 */
export const GROUPS: readonly OptionGroup[] = ["datapay", "direct-access"];

/** Heading text for each {@link OptionGroup}, keyed by group id. */
export const GROUP_LABELS: Record<OptionGroup, string> = {
  datapay: "Datapay Reasons",
  "direct-access": "Direct Access Reasons",
};


// ────────────────────────────────────────────────────────────────────────────────
// Key derivation from generated types
// ────────────────────────────────────────────────────────────────────────────────

/**
 * Boolean feature-flag keys of {@link CompanyOptionsProfile}.
 *
 * `CompanyOptionsProfile` also carries one non-boolean field handled
 * separately: `LeaveRequestEscalation` (number|null). Everything else is an
 * optional boolean. (`PaydayFilingStartDate` was removed from the rendered
 * field list in #347 — it round-trips untouched via the whole-object spread.)
 */
export type CompanyProfileBooleanKey = {
  [K in keyof CompanyOptionsProfile]-?: NonNullable<
    CompanyOptionsProfile[K]
  > extends boolean
    ? K
    : never;
}[keyof CompanyOptionsProfile];

/**
 * Boolean keys of {@link PayGroupOptionsProfile}.
 *
 * Derived directly from the generated type so a renamed backend key fails
 * typecheck at compile time.
 */
export type PayGroupBooleanKey = {
  [K in keyof PayGroupOptionsProfile]-?: NonNullable<
    PayGroupOptionsProfile[K]
  > extends boolean
    ? K
    : never;
}[keyof PayGroupOptionsProfile];

/**
 * Numeric keys of {@link PayGroupOptionsProfile} (excludes booleans and any
 * other non-numeric fields).
 *
 * Derived directly from the generated type so a renamed backend key fails
 * typecheck at compile time.
 */
export type PayGroupNumberKey = {
  [K in keyof PayGroupOptionsProfile]-?: NonNullable<
    PayGroupOptionsProfile[K]
  > extends number
    ? K
    : never;
}[keyof PayGroupOptionsProfile];

/**
 * Validation errors for Pay Group number fields, keyed by the field's
 * {@link PayGroupOptionsProfile} key (e.g. `"DaysAfterTerminationDate"`).
 *
 * Typed to {@link PayGroupNumberKey} (rather than a bare `string` key) so a
 * mistyped error key fails at compile time instead of silently no-op'ing the
 * error display.
 */
export type PgNumberErrors = Partial<Record<PayGroupNumberKey, string>>;

/**
 * Validation errors for Company Settings string fields, keyed by the field's
 * {@link CompanySettingsProfile} string key (currently only
 * `"SetDirectAccessAdditionalResourcesUrl"`).
 *
 * Typed to {@link CompanySettingsStringKey} (rather than a bare `string` key)
 * so a mistyped error key fails at compile time instead of silently no-op'ing
 * the error display.
 */
export type CsErrors = Partial<Record<CompanySettingsStringKey, string>>;

/**
 * Boolean feature-flag keys of {@link CompanySettingsProfile}.
 *
 * `CompanySettingsProfile` carries 14 optional booleans and one URL string
 * (`SetDirectAccessAdditionalResourcesUrl`). This type resolves to exactly
 * the 14 boolean keys. Derived directly from the generated type so a renamed
 * backend key fails typecheck at compile time.
 */
export type CompanySettingsBooleanKey = {
  [K in keyof CompanySettingsProfile]-?: NonNullable<
    CompanySettingsProfile[K]
  > extends boolean
    ? K
    : never;
}[keyof CompanySettingsProfile];

/**
 * `cs-boolean` master keys whose value gates a dependent `cs-boolean` sibling.
 * Maps a parent key → its dependent key.
 *
 * CO-DA-14 (`SendEmailToAdminOnEmployeePasswordReset`) is gated by CO-DA-01
 * (`AllowESSUsersToResetTheirPassword`): when the parent is off, the dependent
 * must be `false` (§5.4 sibling-dependency — a pure UI/client gate, no
 * server-side 422).
 *
 * Single source of truth shared by the editable renderer (`OptionsSection`),
 * the read-only summary (`ProfileSummary`), and the submit-time normalisation
 * in `cleanProfile`, so display and persisted value can't disagree.
 */
export const CS_BOOLEAN_DEPENDENTS: Partial<
  Record<CompanySettingsBooleanKey, CompanySettingsBooleanKey>
> = {
  AllowESSUsersToResetTheirPassword: "SendEmailToAdminOnEmployeePasswordReset",
};

/**
 * Reverse of {@link CS_BOOLEAN_DEPENDENTS}: maps a dependent key → its parent.
 * Used to decide whether a dependent checkbox is disabled/forced-false (when
 * its parent is off).
 */
export const CS_BOOLEAN_PARENT: Partial<
  Record<CompanySettingsBooleanKey, CompanySettingsBooleanKey>
> = {
  SendEmailToAdminOnEmployeePasswordReset: "AllowESSUsersToResetTheirPassword",
};

/**
 * String (URL) keys of {@link CompanySettingsProfile}.
 *
 * Currently the single key `"SetDirectAccessAdditionalResourcesUrl"`.
 * Derived directly from the generated type so a renamed backend key fails
 * typecheck at compile time.
 */
export type CompanySettingsStringKey = {
  [K in keyof CompanySettingsProfile]-?: NonNullable<
    CompanySettingsProfile[K]
  > extends string
    ? K
    : never;
}[keyof CompanySettingsProfile];

/**
 * Boolean keys of {@link PayGroupSettingsProfile}.
 *
 * Currently the single key `"EnableLIW"`. Derived directly from the generated
 * type so a renamed backend key fails typecheck at compile time.
 */
export type PayGroupSettingsBooleanKey = {
  [K in keyof PayGroupSettingsProfile]-?: NonNullable<
    PayGroupSettingsProfile[K]
  > extends boolean
    ? K
    : never;
}[keyof PayGroupSettingsProfile];

// ────────────────────────────────────────────────────────────────────────────────
// Field definition discriminated union
// ────────────────────────────────────────────────────────────────────────────────

/**
 * A single control definition inside a grouped option section.
 *
 * The `kind` discriminant drives how the section renderer draws each row:
 *
 * - `"boolean"` — a checkbox wired to a {@link CompanyOptionsProfile} boolean key.
 * - `"escalation"` — compound leave-request escalation (checkbox + day count),
 *   implicitly wired to `EnableLeaveRequestEscalation` / `LeaveRequestEscalation`.
 * - `"pg-boolean"` — a checkbox wired to a {@link PayGroupOptionsProfile} boolean key.
 * - `"pg-number"` — a number input wired to a {@link PayGroupOptionsProfile} numeric key.
 *   When `parentKey` is set the field is conditional on that parent being `true`.
 *   `min`/`max` impose an optional inclusive range (e.g. 0–23 for hour-of-day fields).
 * - `"cs-boolean"` — a checkbox wired to a {@link CompanySettingsProfile} boolean key.
 *   Folds into the existing "Company options" section; the `kind` discriminant routes
 *   reads/writes to the `CompanySettings` sub-profile rather than `CompanyOptions`.
 * - `"cs-string"` — a text input wired to a {@link CompanySettingsProfile} string key
 *   (currently only `SetDirectAccessAdditionalResourcesUrl` — a URL field).
 *   Folds into the existing "Company options" section.
 * - `"pg-settings-boolean"` — a checkbox wired to a {@link PayGroupSettingsProfile}
 *   boolean key. Folds into the existing "Pay Group options" section; the `kind`
 *   discriminant routes reads/writes to the `PayGroupSettings` sub-profile rather than
 *   `PayGroupOptions`.
 */
export type OptionFieldDef =
  | { kind: "boolean"; key: CompanyProfileBooleanKey; label: string; group: OptionGroup }
  | { kind: "escalation"; label: string; group: OptionGroup }
  | { kind: "pg-boolean"; key: PayGroupBooleanKey; label: string; group: OptionGroup }
  | {
      kind: "pg-number";
      key: PayGroupNumberKey;
      label: string;
      group: OptionGroup;
      /** When set, the field is visible only when this parent boolean is `true`. */
      parentKey?: PayGroupBooleanKey;
      /** Optional lower bound (inclusive). */
      min?: number;
      /** Optional upper bound (inclusive). */
      max?: number;
    }
  | { kind: "cs-boolean"; key: CompanySettingsBooleanKey; label: string; group: OptionGroup }
  | { kind: "cs-string"; key: CompanySettingsStringKey; label: string; group: OptionGroup }
  | {
      kind: "pg-settings-boolean";
      key: PayGroupSettingsBooleanKey;
      label: string;
      group: OptionGroup;
    };

// ────────────────────────────────────────────────────────────────────────────────
// Section definition
// ────────────────────────────────────────────────────────────────────────────────

/**
 * A grouped option section rendered by `OptionsSection`.
 *
 * Each section binds to one sub-profile key on `CompanyConfigProfileInput`.
 */
export interface OptionSection {
  /** Unique identifier. */
  id: string;
  /** Section heading shown to the user. */
  title: string;
  /** All field definitions for this section (across both groups). */
  fields: readonly OptionFieldDef[];
  /** Which sub-profile key on `CompanyConfigProfileInput` this section writes to. */
  profileKey: "CompanyOptions" | "PayGroupOptions";
  /**
   * Optional secondary profile key — the section is shown when *either* the
   * primary or the secondary key is present on the profile.
   *
   * Used for "Pay Group options" (`secondaryProfileKey: "PayGroupSettings"`) so
   * that a config carrying `PayGroupSettings` (e.g. `EnableLIW` was set) but no
   * `PayGroupOptions` still shows the section in `ProfileSummary`. Fields bound
   * to the absent primary sub-profile gracefully default to Off / "—" via
   * optional-chaining.
   */
  secondaryProfileKey?: "CompanySettings" | "PayGroupSettings";
  /** Both sections are live. */
  enabled: boolean;
}

// ────────────────────────────────────────────────────────────────────────────────
// Company option fields — relabelled + grouped (verbatim Christmas UI labels)
// ────────────────────────────────────────────────────────────────────────────────

/**
 * All editable Company option field definitions, keyed to
 * {@link CompanyOptionsProfile}.
 *
 * **Label source:** verbatim Christmas UI (`CompanyOptionsView.cs`) reconciled
 * against the SME matrix v0.1 (Jul 2026).
 *
 * **Grouping:** Julia blue-dot = Direct Access; `DirectAccess`/`DA`/`ESS` in
 * key name = Direct Access (rule-2); sub-controls inherit their parent's group
 * (rule-3); default = Datapay (rule-4).
 *
 * **Fields confirmed excluded from this list** (Jul 2026 audit of
 * `CompanyOptionsView.cs` found no verbatim NZ Christmas UI label for these
 * keys — they are intentionally not rendered):
 * `EnableLeaveHoursIfNoWorkPattern`, `SkipUserActivationEmails`,
 * `ViewAsPersonStructure`, `SendReportViaDownloadLink`, `EnableSTPVersion2`,
 * `EnableSmsActivationMobileUsers`, `EnableTerminationWizardInDA`.
 * These keys are still present on `CompanyOptionsProfile` and round-trip untouched
 * (the form spreads the whole `CompanyOptions` object, so un-rendered flags are
 * preserved on edit and default to `false` on create).
 *
 * **R-OPT-2 exclusions (Web#431 / Product-PRDs#350):** three fields are
 * deliberately hidden from the editable UI pending Product/SME clarification —
 * none render a checkbox or control for them, and all three round-trip
 * untouched like the keys above:
 * - `EnableMultiCompanyEmployeeGroups` ("Enable Multi-Company Employee Groups")
 *   — removed from this list by R-OPT-2 (previously rendered; see git history
 *   for the prior field definition).
 * - `EnableLatestRuleTemplate` — already excluded (see the CO-DP-23 note
 *   below); R-OPT-2 confirms it stays hidden rather than being added under a
 *   corrected label.
 * - `PayGroupOptionsProfile.PayrollConsultant` — a `PayrollConsultantRef`
 *   object (not a boolean/number), so `OptionFieldDef` has no `kind` that can
 *   render it; it was never exposed by the UI and R-OPT-2 confirms that stays
 *   the case for now.
 *
 * **Matrix reconciliation notes** (CO-DP-02 now rendered; CO-DP-23 still blocked —
 * confirmed against
 * `CompanyOptionsView.cs`, Jul 2026):
 * - **CO-DP-02** "Automatically Calculate Public Holidays to Pay on Termination" is
 *   now rendered — its real backing field `CompanySettings.PayPublicHolidays` was
 *   added by the backend, so it lives in {@link COMPANY_SETTINGS_FIELDS} (Datapay).
 *   The former `CompanyOptions.AutoCalculateDaysPaid` (a dead/orphaned control that
 *   this label was once mislabelled onto) has been removed from the generated client.
 * - **CO-DP-23** "Use Earliest Rule Template Link Start Date to Roll For Non-Leave
 *   Rule Templates" remains **unrenderable**: its real field
 *   `company.UseEarliestRuleTemplateLinkStartForOtherRuleTemplates` is **still not on
 *   the generated client**. Only the unrelated `EnableLatestRuleTemplate` checkbox
 *   (`_enableLatestRuleTemplate`, hidden in NZ, flagged for deletion in Christmas) is
 *   exposed — it is a different control, not the matrix field, so there is nothing to
 *   render for CO-DP-23 until the backend exposes that key. `EnableLatestRuleTemplate`
 *   remains on `CompanyOptionsProfile` and round-trips untouched, like the excluded
 *   keys above.
 */
export const PROFILE_FIELDS: readonly OptionFieldDef[] = [
  // ── Datapay (19 booleans; R-OPT-2 hid EnableMultiCompanyEmployeeGroups) ──────
  {
    kind: "boolean",
    key: "AvoidReportCache",
    label: "Avoid using ReportCache for some Reports",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EmployeesCanViewOrganisationStructure",
    label: "Company Organisation Structure Visible to Employees",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableCompanyGroupAuthorisationDistribution",
    label: "Enable Company Group Authorisation",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableCopyNoMerge",
    // Christmas label confirmed by SME (2026-07-29): the API's `EnableCopyNoMerge`
    // is the "Don't Merge PDF When SFTP" checkbox.
    label: "Don't Merge PDF When SFTP",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableDirectoryCreation",
    label: "Enable File Destination Directory Creation",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableEmployeeJobClassification",
    label: "Enable Employee Job Classification",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableEmployeePayslipsSFTP",
    label: "Enable Employee Payslips SFTP",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableGenerateMultipleBankFiles",
    label: "Generate Multiple Bank Files",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableManualLeavePriority",
    label: "Enable Leave Priorities For Manual PayPacket Components",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableTwoFactorAuthentication",
    label: "Enable Two-factor Authentication",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "ExcludeZeroedBankAccountsInBankFile",
    label: "Exclude Zeroed Bank Accounts in Bank File",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "HidePayPacketWarnings",
    label: "Hide Pay Packet Warnings",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "ManagerReporteeAccessToPaySlipHistoryAndLeaveBalances",
    label: "Manager Reportee Access to Payslips/Leave Balances",
    // ⚠ Group pending Julia blue-dot confirmation: this is conceptually an
    // ESS/Direct-Access feature, but the key has no DA/ESS literal, so it
    // defaults to Datapay (rule-4). Move to "direct-access" if the blue-dot
    // catalogue marks it so.
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "PayPacketTimesheetApportioning",
    label: "Enable BackPay Timesheet Apportioning",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "RemoveCompanyCodeCheckFromTimesheet",
    label: "Remove Company Code Check from Timesheet Name",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "UseCompanyMainContactForSTPDeclarer",
    label: "Use Company Main Contact for Payer Declarer Identifier",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnablePayrollAssistant",
    label: "Enable Payroll Assistant",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "EnableMultiJobbing",
    label: "Enable Multi Jobbing",
    group: "datapay",
  },
  {
    kind: "boolean",
    key: "UseUTF8WithBomReportEncoding",
    label: "Use UTF8 Encoding for CSV and GL Reports",
    group: "datapay",
  },
  // ── Direct Access (6 booleans + 1 escalation compound) ──────────────────────
  {
    kind: "boolean",
    key: "AutoMapEmployeeGroupsForNewEmployees",
    label: "Auto Map Employee Groups for New Employees",
    group: "direct-access",
  },
  {
    kind: "boolean",
    key: "DisableEmployeeModificationInDA",
    label: "Disable Employee Modification in Direct Access",
    group: "direct-access",
  },
  {
    kind: "boolean",
    key: "DisableNewEmployeeWizardInDA",
    label: "Disable New Employee Wizard in Direct Access",
    group: "direct-access",
  },
  {
    kind: "escalation",
    label: "Enable Leave Request Escalation after x days",
    group: "direct-access",
  },
  {
    kind: "boolean",
    key: "EnableOtherPaymentsInDA",
    label: "Enable Other Payments In Direct Access",
    group: "direct-access",
  },
  {
    kind: "boolean",
    key: "HidePeersLeaveInDA",
    label: "Hide Peers Leave in DirectAccess",
    group: "direct-access",
  },
];

// ────────────────────────────────────────────────────────────────────────────────
// Company Settings fields — 14 booleans + 1 URL string, typed to CompanySettingsProfile
// ────────────────────────────────────────────────────────────────────────────────

/**
 * All editable Company Settings field definitions, keyed to
 * {@link CompanySettingsProfile} (14 booleans + 1 URL string).
 *
 * **Data-routing concern only — no new UI sub-section.** These fields fold into
 * the existing "Company options" section. The `kind` discriminant (`cs-boolean`
 * / `cs-string`) routes reads/writes to `CompanySettings` rather than
 * `CompanyOptions`. `fieldsForGroup` sorts them A→Z within their group, so they
 * interleave with the existing `CompanyOptions` booleans under the right tab.
 *
 * **Label source:** verbatim Christmas UI labels from the SME matrix v0.1
 * (Jul 2026). Labels deliberately diverge from backend key names.
 *
 * **Grouping:** Julia blue-dot = Direct Access; DA/ESS in key = Direct Access;
 * default = Datapay.
 */
export const COMPANY_SETTINGS_FIELDS: readonly OptionFieldDef[] = [
  // ── Direct Access (CO-DA-01..15) ─────────────────────────────────────────
  {
    kind: "cs-boolean",
    key: "AllowESSUsersToResetTheirPassword",
    label: "Allow ESS Users to Reset their Password",
    group: "direct-access",
  },
  {
    kind: "cs-boolean",
    key: "EmailESSPaySlipsOnDCDate",
    label: "Email ESS Payslips on DC Date instead of Finalisation Date",
    group: "direct-access",
  },
  {
    kind: "cs-boolean",
    key: "EnableDirectAccess",
    label: "Enable Direct Access",
    group: "direct-access",
  },
  {
    kind: "cs-boolean",
    key: "HideESSURL",
    label: "Hide URL on ESS notifications",
    group: "direct-access",
  },
  {
    kind: "cs-boolean",
    key: "UseLeaveReasons",
    label: "Leave Reasons for ESS Leave Requests",
    group: "direct-access",
  },
  {
    kind: "cs-boolean",
    key: "UseSpecifiedLeaveAmountPerPayPeriod",
    label: "Leave Requests — Ability to specify leave amount per Pay Period",
    group: "direct-access",
  },
  {
    // CO-DA-14: dependent on CO-DA-01 (AllowESSUsersToResetTheirPassword).
    // The UI disables + auto-clears this when the parent is off.
    // Coupling implemented in Step D6 (OptionsSection renderer).
    kind: "cs-boolean",
    key: "SendEmailToAdminOnEmployeePasswordReset",
    label: "Send Email to Admin on Employee Password Reset",
    group: "direct-access",
  },
  {
    // CO-DA-13: non-inverted (PasswordProtectPayslipHistory = true means
    // protection ON = checkbox ON). Previously bound to the inverted
    // `CompanyOptions.SendPayslipsWithoutPassword`, which the backend removed.
    kind: "cs-boolean",
    key: "PasswordProtectPayslipHistory",
    label: "Password Protection for Saved ESS Payslips",
    group: "direct-access",
  },
  {
    kind: "cs-string",
    key: "SetDirectAccessAdditionalResourcesUrl",
    label: "Additional Resources URL",
    group: "direct-access",
  },

  // ── Datapay (CO-DP-01..27) ───────────────────────────────────────────────
  {
    kind: "cs-boolean",
    key: "AdvancedSorting",
    label: "Advanced Text Sorting",
    group: "datapay",
  },
  {
    // CO-DP-02: real backing field. The former `CompanyOptions.AutoCalculateDaysPaid`
    // (a dead/orphaned control) was removed by the backend; this label now binds to
    // `CompanySettings.PayPublicHolidays`.
    kind: "cs-boolean",
    key: "PayPublicHolidays",
    label: "Automatically Calculate Public Holidays to Pay on Termination",
    group: "datapay",
  },
  {
    kind: "cs-boolean",
    key: "ExcludeFromACRFile",
    label: "Exclude from ACR file",
    group: "datapay",
  },
  {
    kind: "cs-boolean",
    key: "UseTerminationRequests",
    label: "Use Automated Termination Payments",
    group: "datapay",
  },
  {
    kind: "cs-boolean",
    key: "UseCompressedNewEmployeeForm",
    label: "Use Updated New Employee Form",
    group: "datapay",
  },
  {
    // CO-DP-27: The Christmas label deliberately does NOT echo the backend key
    // (`EnableTrialComponentVersions`). Christmas uses the exact same label,
    // verbatim, for the pay-group-level counterpart
    // (`PayGroupSettings.EnableLIW`, see `PAY_GROUP_SETTINGS_FIELDS`) — no
    // suffix. The two are disambiguated only by which sub-section they render
    // under ("Company options" vs "Pay Group options"), never by label text.
    kind: "cs-boolean",
    key: "EnableTrialComponentVersions",
    label: "Enable Annual Leave in Weeks Calculations",
    group: "datapay",
  },
];

// ────────────────────────────────────────────────────────────────────────────────
// Pay Group option fields — all 18 live (v1), typed to PayGroupOptionsProfile
// ────────────────────────────────────────────────────────────────────────────────

/**
 * All editable Pay Group option field definitions, keyed to
 * {@link PayGroupOptionsProfile}.
 *
 * **Label source:** verbatim Christmas UI (`PayGroupOptionsView.cs`) reconciled
 * against the SME matrix v0.1 (Jul 2026).
 *
 * **Grouping:** Julia blue-dot = Direct Access; default = Datapay (rule-4).
 *
 * Sub-controls (↳) are rendered indented only when their `parentKey` boolean
 * is `true`, mirroring the escalation-days pattern for Company options.
 *
 * **`EnableLIW` (Enable Annual Leave in Weeks Calculations):** Previously excluded
 * because it maps to `PayGroup.EnableLIW` on the `PayGroup` entity model (not
 * `PayGroupOptionsProfile`). As of Pacific.Agentic.Api#886, `EnableLIW` is now
 * exposed via `PayGroupSettingsProfile` and delivered as a `pg-settings-boolean` in
 * {@link PAY_GROUP_SETTINGS_FIELDS}, folded into the "Pay Group options" section.
 * Its label is identical, verbatim, to `CompanySettings.EnableTrialComponentVersions`'s
 * — Christmas uses the same label for both; only the section heading distinguishes them.
 */
export const PAY_GROUP_FIELDS: readonly OptionFieldDef[] = [
  // ── Direct Access (Julia blue dots) — 3 checkboxes + 1 conditional number ──
  {
    kind: "pg-boolean",
    key: "AutoEndDateUserAfterTermination",
    label: "Auto End Date User After Termination",
    group: "direct-access",
  },
  {
    kind: "pg-number",
    key: "DaysAfterTerminationDate",
    label: "Days After Termination Date",
    group: "direct-access",
    parentKey: "AutoEndDateUserAfterTermination",
    min: 1,
  },
  {
    kind: "pg-boolean",
    key: "EmailUserOnTimesheetImportJobCompletion",
    label: "Email User On Timesheet Import Job Completion",
    group: "direct-access",
  },
  {
    kind: "pg-boolean",
    key: "UseDirectAccessURLForEmailNotification",
    label: "Use Direct Access URL For Email Notification",
    group: "direct-access",
  },

  // ── Datapay — 9 checkboxes + 2 standalone numbers + 2 conditional numbers ──
  {
    kind: "pg-number",
    key: "NewPayRunCreationWaitTime",
    label: "Auto Main Pay Run Creation Wait Time (days)",
    group: "datapay",
  },
  {
    kind: "pg-number",
    key: "FinalReportDistributionProcessTime",
    label: "Final Report Distribution Time",
    group: "datapay",
    // Bounded byte — hour-of-day 0–23 (NOT a time string; empty → null = immediate).
    min: 0,
    max: 23,
  },
  {
    kind: "pg-boolean",
    key: "EmailAllPayrollAdminsForAPIOrThirdPartyImportedTimesheets",
    label: "Email All Payroll Admins For API or Third-Party Imported Timesheets",
    group: "datapay",
  },
  {
    kind: "pg-boolean",
    key: "EnablePayRunAuthorisation",
    label: "Enable Pay Run Authorisation",
    group: "datapay",
  },
  {
    kind: "pg-number",
    key: "MinNumOfPayRunAuthorisers",
    label: "Minimum Number of Pay Run Authorisers",
    group: "datapay",
    // Conditional: > 0 required when EnablePayRunAuthorisation is on.
    parentKey: "EnablePayRunAuthorisation",
    min: 1,
  },
  {
    kind: "pg-boolean",
    key: "StopAutomaticTimesheetGeneration",
    label: "Stop Automatic Timesheet Generation",
    group: "datapay",
  },
  {
    kind: "pg-boolean",
    key: "AllowManualCreationOfEmptyPendingRuns",
    label: "Allow Manual Creation of Empty Pending Runs",
    group: "datapay",
  },
  {
    kind: "pg-boolean",
    key: "AutoCreateEmptyPendingRuns",
    label: "Auto Create Empty Pending Runs",
    group: "datapay",
  },
  {
    kind: "pg-boolean",
    key: "AutomaticallyFinaliseMainPayRun",
    label: "Automatically Finalise Main Pay Run after x days",
    group: "datapay",
  },
  {
    kind: "pg-number",
    key: "DaysToFinaliseAfterDCDate",
    label: "Days After DC Date",
    group: "datapay",
    // Conditional: > 0 required when AutomaticallyFinaliseMainPayRun is on.
    parentKey: "AutomaticallyFinaliseMainPayRun",
    min: 1,
  },
  {
    kind: "pg-boolean",
    key: "AutomaticallyGenerateTimesheet",
    label: "Automatically Generate Timesheet",
    group: "datapay",
    // Note: Christmas renders this read-only (Enabled=false), but the Agentic
    // API exposes it as a normal settable boolean. Decision (Jul 2026): kept
    // editable. Revisit with PS if it should be hidden/read-only.
  },
  {
    kind: "pg-boolean",
    key: "DisablePayRunsPublicHolidayDCDateShift",
    label: "Disable Pay Runs Public Holiday DC Date Shift",
    group: "datapay",
  },
  {
    kind: "pg-boolean",
    key: "LoadTop50PrioritisedPayPackets",
    label: "Load Top 50 Prioritised Pay Packets",
    group: "datapay",
  },
];

// ────────────────────────────────────────────────────────────────────────────────
// Pay Group Settings fields — 1 boolean, typed to PayGroupSettingsProfile
// ────────────────────────────────────────────────────────────────────────────────

/**
 * All editable Pay Group Settings field definitions, keyed to
 * {@link PayGroupSettingsProfile} (currently 1 boolean: `EnableLIW`).
 *
 * **Data-routing concern only — no new UI sub-section.** These fields fold into
 * the existing "Pay Group options" section. The `kind` discriminant
 * (`pg-settings-boolean`) routes reads/writes to `PayGroupSettings` rather than
 * `PayGroupOptions`. `fieldsForGroup` sorts this entry A→Z within its group,
 * so it interleaves with the existing `PayGroupOptions` booleans under the
 * Datapay tab.
 *
 * **Label source:** verbatim Christmas UI label from the SME matrix v0.1
 * (Jul 2026). The label deliberately diverges from the backend key name
 * (`EnableLIW`). Christmas uses this exact label, verbatim, for the
 * company-level counterpart too (`CompanySettings.EnableTrialComponentVersions`,
 * rendered under "Company options") — no disambiguating suffix. The two entries
 * are told apart only by which sub-section they render under, never by label text.
 */
export const PAY_GROUP_SETTINGS_FIELDS: readonly OptionFieldDef[] = [
  // ── Datapay (PG-DP-09) ────────────────────────────────────────────────────
  {
    // PG-DP-09: Christmas label deliberately does NOT echo the backend key
    // (`EnableLIW`). Shares its exact label, verbatim, with
    // `CompanySettings.EnableTrialComponentVersions` (company-level, renders
    // under "Company options") — no suffix; the section heading is the only
    // disambiguator.
    kind: "pg-settings-boolean",
    key: "EnableLIW",
    label: "Enable Annual Leave in Weeks Calculations",
    group: "datapay",
  },
];

// ────────────────────────────────────────────────────────────────────────────────
// Section registry
// ────────────────────────────────────────────────────────────────────────────────

/**
 * Ordered list of sections rendered by the copy-settings-profile editor.
 *
 * Each entry maps to one `OptionsSection` component and one sub-profile key on
 * `CompanyConfigProfileInput`. Extensibility: adding a future section is one
 * array entry here plus a matching sub-profile key on the generated DTO.
 *
 * **Ordering:** Company options first, Pay Group options second.
 *
 * **Folding pattern:** `CompanySettings` fields ({@link COMPANY_SETTINGS_FIELDS})
 * are folded into the "Company options" section (no new sub-section). Likewise,
 * `PayGroupSettings` fields ({@link PAY_GROUP_SETTINGS_FIELDS}) fold into
 * "Pay Group options". The `profileKey` values stay `"CompanyOptions"` /
 * `"PayGroupOptions"` (used only by the summary's absent-guard); each field's
 * `kind` discriminant routes its value to the correct sub-profile at
 * read/write time. `fieldsForGroup` sorts the combined list A→Z within each
 * group, so the new booleans interleave with the existing ones under the right
 * provider tab.
 */
export const OPTION_SECTIONS: readonly OptionSection[] = [
  {
    id: "company-options",
    title: "Company options",
    fields: [...PROFILE_FIELDS, ...COMPANY_SETTINGS_FIELDS],
    profileKey: "CompanyOptions",
    enabled: true,
  },
  {
    id: "pay-group-options",
    title: "Pay Group options",
    fields: [...PAY_GROUP_FIELDS, ...PAY_GROUP_SETTINGS_FIELDS],
    profileKey: "PayGroupOptions",
    // Show the section even when PayGroupOptions is absent, provided
    // PayGroupSettings is present (e.g. only EnableLIW was set).
    secondaryProfileKey: "PayGroupSettings",
    enabled: true,
  },
];

/**
 * The `enabled` subset of {@link OPTION_SECTIONS}, in registry order.
 *
 * Single source of truth for both `CopySettingsProfile` (editable) and
 * `ProfileSummary` (read-only) so a section toggled off can't be dropped from
 * one and forgotten in the other.
 */
export function enabledSections(): readonly OptionSection[] {
  return OPTION_SECTIONS.filter((section) => section.enabled);
}

// ────────────────────────────────────────────────────────────────────────────────
// Empty profile factories
// ────────────────────────────────────────────────────────────────────────────────

/**
 * A fresh Company options profile for a new configuration.
 *
 * Every boolean rendered by the form (each `kind: "boolean"` field in
 * {@link PROFILE_FIELDS}, plus `EnableLeaveRequestEscalation`) is explicitly
 * `false`; `LeaveRequestEscalation` is cleared to `null`. Keys that the form
 * does not render (the 7 excluded booleans with no confirmed Christmas label,
 * plus `PaydayFilingStartDate` removed in #347) are intentionally left
 * `undefined` — still falsy but not sent as explicit `false` on create, and
 * they round-trip untouched on edit.
 */
export function emptyProfile(): CompanyOptionsProfile {
  const profile: CompanyOptionsProfile = {
    LeaveRequestEscalation: null,
    EnableLeaveRequestEscalation: false,
  };
  for (const field of PROFILE_FIELDS) {
    if (field.kind === "boolean") {
      profile[field.key] = false;
    }
  }
  return profile;
}

/**
 * A fresh, fully-empty Pay Group options profile.
 *
 * Returns an empty object so the caller (the form) can lazy-initialise
 * `PayGroupOptions` the first time a Pay Group control changes, without
 * forcing an empty object onto create payloads.
 */
export function emptyPayGroupProfile(): PayGroupOptionsProfile {
  return {};
}

/**
 * A fresh Company Settings profile for a new configuration.
 *
 * Every boolean rendered by the form (each `kind: "cs-boolean"` field in
 * {@link COMPANY_SETTINGS_FIELDS}) is explicitly `false`; the URL field
 * (`SetDirectAccessAdditionalResourcesUrl`) is cleared to `null`. Keys match
 * exactly the 14 booleans + 1 URL string in the generated
 * {@link CompanySettingsProfile} — no excluded-key nuance applies.
 */
export function emptyCompanySettingsProfile(): CompanySettingsProfile {
  const profile: CompanySettingsProfile = {
    SetDirectAccessAdditionalResourcesUrl: null,
  };
  for (const field of COMPANY_SETTINGS_FIELDS) {
    if (field.kind === "cs-boolean") {
      profile[field.key] = false;
    }
  }
  return profile;
}

/**
 * A fresh Pay Group Settings profile for a new configuration.
 *
 * `EnableLIW` is explicitly `false` — the single boolean in
 * {@link PAY_GROUP_SETTINGS_FIELDS}. Uses the same iteration pattern as
 * {@link emptyProfile} / {@link emptyCompanySettingsProfile} for future-proofing
 * if additional `PayGroupSettingsProfile` keys are added.
 */
export function emptyPayGroupSettingsProfile(): PayGroupSettingsProfile {
  const profile: PayGroupSettingsProfile = {};
  for (const field of PAY_GROUP_SETTINGS_FIELDS) {
    if (field.kind === "pg-settings-boolean") {
      profile[field.key] = false;
    }
  }
  return profile;
}
